import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const getToken = () => localStorage.getItem('token')

const parseApiError = async (response: Response, fallback: string) => {
  const text = await response.text()
  try {
    const json = JSON.parse(text)
    return json.error || fallback
  } catch {
    if (text.includes('<!DOCTYPE') || text.includes('<html')) {
      return 'Server sedang tidak tersedia. Coba lagi beberapa saat.'
    }
    return text.slice(0, 200) || fallback
  }
}

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getToken()
  const response = await fetch(`${API_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    }
  })
  
  if (!response.ok) {
    const message = await parseApiError(response, 'Request failed')
    throw new Error(message)
  }
  
  return response.json()
}

// Brands
export const useBrands = () => {
  return useQuery({
    queryKey: ['brands'],
    queryFn: () => fetchWithAuth('/brands')
  })
}

export const useMyBrand = () => {
  return useQuery({
    queryKey: ['myBrand'],
    queryFn: () => fetchWithAuth('/brands/me')
  })
}

export const useCreateBrand = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: any) => fetchWithAuth('/brands', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brands'] })
      queryClient.invalidateQueries({ queryKey: ['myBrand'] })
      const isCategory = Boolean(data?.isCategoryBrand)
      toast.success(isCategory ? 'Kategori brand berhasil ditambahkan' : 'Brand berhasil dibuat')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

// Products
export const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: () => fetchWithAuth('/products')
  })
}

export const useCreateProduct = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: any) => fetchWithAuth('/products', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Product created successfully')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

// Increment brand view count (public)
export const incrementBrandView = async (brandId: number | string) => {
  const response = await fetch(`${API_URL}/brands/${brandId}/view`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  })
  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to increment view: ${errorText}`)
  }
  return response.json()
}

// Subscriptions
export const useSubscriptions = (enabled = true) => {
  return useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => fetchWithAuth('/subscriptions'),
    enabled,
  })
}

// Upload
export const useUploadImage = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (file: File) => {
      const token = getToken()
      const formData = new FormData()
      formData.append('image', file)
      
      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: formData
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }
      
      return response.json()
    },
    onSuccess: () => {
      toast.success('Image uploaded successfully')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

export const useUploadVideo = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const token = getToken()
      const formData = new FormData()
      formData.append('video', file)

      const response = await fetch(`${API_URL}/upload/video`, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: formData
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }

      return response.json()
    },
    onSuccess: () => {
      toast.success('Video berhasil diunggah')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    }
  })
}

// Payments
export const useCreatePayment = () => {
  return useMutation({
    mutationFn: (data: { amount: number; paymentType: string; plan?: string }) =>
      fetchWithAuth('/payments/create', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: (data) => {
      if (data.redirectUrl) {
        window.open(data.redirectUrl, '_blank')
      }
    },
    onError: (error: any) => {
      toast.error(error.message)
    },
  })
}

export const usePaymentHistory = () => {
  return useQuery({
    queryKey: ['payments', 'history'],
    queryFn: () => fetchWithAuth('/payments/history'),
  })
}

// Analytics (Premium)
export const useAnalytics = (enabled = true) => {
  return useQuery({
    queryKey: ['analytics'],
    queryFn: () => fetchWithAuth('/analytics'),
    enabled,
    retry: false,
  })
}

// Brand articles
export const useBrandArticles = () => {
  return useQuery({
    queryKey: ['brandArticles'],
    queryFn: () => fetchWithAuth('/brand-articles'),
  })
}

export const useCreateBrandArticle = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { title: string; content: string; excerpt?: string; coverImageUrl?: string }) =>
      fetchWithAuth('/brand-articles', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandArticles'] })
      toast.success('Artikel berhasil dibuat')
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

export const useDeleteBrandArticle = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/brand-articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brandArticles'] })
      toast.success('Artikel dihapus')
    },
    onError: (error: Error) => toast.error(error.message),
  })
}

export const trackWhatsAppClick = async (brandId: number | string) => {
  await fetch(`${API_URL}/brands/${brandId}/whatsapp-click`, { method: 'POST' })
}

export const trackProductView = async (productId: number) => {
  await fetch(`${API_URL}/products/${productId}/view`, { method: 'POST' })
}

// Delete Brand
export const useDeleteBrand = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/brands/${id}`, {
      method: 'DELETE',
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] })
      queryClient.invalidateQueries({ queryKey: ['myBrand'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Brand berhasil dihapus')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

// Update Brand
export const useUpdateBrand = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => fetchWithAuth(`/brands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] })
      queryClient.invalidateQueries({ queryKey: ['myBrand'] })
      toast.success('Brand updated successfully')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

// Update Product
export const useUpdateProduct = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => fetchWithAuth(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Produk berhasil diupdate')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

// Delete Product
export const useDeleteProduct = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/products/${id}`, {
      method: 'DELETE'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Produk berhasil dihapus')
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

// Toggle Product Status
export const useToggleProductStatus = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (id: number) => fetchWithAuth(`/products/${id}/toggle`, {
      method: 'PATCH'
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      const status = data.isActive ? 'aktif' : 'non-aktif'
      toast.success(`Produk sekarang ${status}`)
    },
    onError: (error: any) => {
      toast.error(error.message)
    }
  })
}

const fetchPublic = async (url: string) => {
  const response = await fetch(`${API_URL}${url}`)
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Request failed')
  }
  return response.json()
}

export type ArticlePreview = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  coverImageUrl: string | null
  publishedAt: string | null
  createdAt: string
}

export type ArticleDetail = ArticlePreview & {
  content: string
  metaDescription: string | null
}

export type PublicTestimonial = {
  id: number
  displayName: string
  businessName: string | null
  content: string
  rating: number
  createdAt: string
}

export const usePublicArticles = (limit = 20) => {
  return useQuery({
    queryKey: ['articles', 'public', limit],
    queryFn: async () => {
      try {
        return await fetchPublic(`/articles/public?limit=${limit}`)
      } catch {
        return [] as ArticlePreview[]
      }
    },
  })
}

export const usePublicArticle = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['articles', 'public', slug],
    queryFn: () => fetchPublic(`/articles/public/${slug}`),
    enabled: !!slug,
  })
}

export const usePublicTestimonials = (limit = 12) => {
  return useQuery({
    queryKey: ['testimonials', 'public', limit],
    queryFn: async () => {
      try {
        return await fetchPublic(`/testimonials/public?limit=${limit}`)
      } catch {
        return [] as PublicTestimonial[]
      }
    },
  })
}

export const useMyTestimonial = () => {
  return useQuery({
    queryKey: ['testimonials', 'me'],
    queryFn: () => fetchWithAuth('/testimonials/me'),
  })
}

export const useSaveTestimonial = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      displayName?: string
      businessName?: string
      content: string
      rating?: number
    }) =>
      fetchWithAuth('/testimonials', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] })
      toast.success('Testimoni berhasil disimpan')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

export const useDeleteTestimonial = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => fetchWithAuth('/testimonials/me', { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['testimonials'] })
      toast.success('Testimoni dihapus')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}
